/**
 * License Viewer Component
 * 
 * Full-page view of a single license
 * Shows video player, analysis results, and license details
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getGrant } from '../../utils/accessGrantHelpers';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './LicenseViewer.css';

// Component to display owner's message from the access request
const OwnerMessageDisplay = ({ requestId }) => {
  const [ownerMessage, setOwnerMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRequest = async () => {
      if (!requestId) {
        setLoading(false);
        return;
      }

      try {
        const requestDoc = await getDoc(doc(db, 'accessRequests', requestId));
        if (requestDoc.exists()) {
          const data = requestDoc.data();
          if (data.ownerResponse) {
            setOwnerMessage(data.ownerResponse);
          }
        }
      } catch (err) {
        console.error('Error fetching request message:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [requestId]);

  if (loading || !ownerMessage) return null;

  return (
    <div className="owner-message-box">
      <div className="message-header">
        <span className="message-icon">💬</span>
        <strong>Message from Owner:</strong>
      </div>
      <p className="message-text">"{ownerMessage}"</p>
    </div>
  );
};

const LicenseViewer = ({ user }) => {
  const { licenseId } = useParams();
  const navigate = useNavigate();
  const [license, setLicense] = useState(null);
  const [job, setJob] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchLicenseData = async () => {
      try {
        // Get the grant
        const grantData = await getGrant(licenseId);
        
        if (!grantData) {
          setError('License not found');
          setLoading(false);
          return;
        }

        // Verify this license belongs to current user
        if (grantData.userId !== user.uid) {
          setError('Access denied - this license belongs to another user');
          setLoading(false);
          return;
        }

        // Check if still active
        if (!grantData.isActive) {
          setError('This license has been revoked');
          setLoading(false);
          return;
        }

        setLicense(grantData);

        // Fetch job data
        const jobDoc = await getDoc(doc(db, 'jobs', grantData.jobId));
        if (jobDoc.exists()) {
          setJob({ id: jobDoc.id, ...jobDoc.data() });
        } else {
          setError('Job not found');
          setLoading(false);
          return;
        }

        // Fetch evidence data
        const evidenceRef = doc(db, 'casefiles', grantData.casefileId, 'evidence', grantData.evidenceId);
        const evidenceDoc = await getDoc(evidenceRef);
        if (evidenceDoc.exists()) {
          setEvidence({ id: evidenceDoc.id, ...evidenceDoc.data() });
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching license:', err);
        setError('Failed to load license: ' + err.message);
        setLoading(false);
      }
    };

    fetchLicenseData();
  }, [licenseId, user, navigate]);

  const getGrantTypeLabel = (grantType) => {
    switch (grantType) {
      case 'owner':
        return '👤 You own this job';
      case 'request_approved':
        return '✅ Free access granted by owner';
      case 'purchase':
        return '💳 Purchased license';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="license-viewer">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading license...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="license-viewer">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h2>{error}</h2>
          <button onClick={() => navigate('/licenses')} className="back-btn">
            ← Back to My Licenses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="license-viewer">
      {/* Header */}
      <div className="viewer-header">
        <button onClick={() => navigate('/licenses')} className="back-btn">
          ← Back to Licenses
        </button>
        <h1>Licensed Content</h1>
        <div className="license-type-badge">
          {getGrantTypeLabel(license.grantType)}
        </div>
      </div>

      {/* License Info Banner */}
      <div className="license-info-banner">
        <div className="info-item">
          <strong>Source:</strong>{' '}
          {license.showCasefileName 
            ? license.casefileName 
            : 'Private Investigation'}
        </div>
        <div className="info-item">
          <strong>Granted:</strong>{' '}
          {license.createdAt?.toDate().toLocaleString()}
        </div>
        {license.price && (
          <div className="info-item">
            <strong>Price Paid:</strong> ${license.price}
          </div>
        )}
        {license.revocable && (
          <div className="info-item warning">
            ⚠️ This access can be revoked by the owner
          </div>
        )}
      </div>

      {/* Owner Message (for request_approved grants) */}
      {license.grantType === 'request_approved' && license.requestId && (
        <OwnerMessageDisplay requestId={license.requestId} />
      )}

      {/* Main Content Area */}
      <div className="viewer-content">
        {/* Video Section */}
        {license.permissions?.viewVideo && evidence && (
          <div className="content-section video-section">
            <h2>📹 Evidence Video</h2>
            {evidence.downloadURL ? (
              <div className="video-player-container">
                <video 
                  controls 
                  src={evidence.downloadURL}
                  className="license-video"
                  controlsList="nodownload"
                >
                  Your browser does not support video playback.
                </video>
                <div className="video-info">
                  <p><strong>Filename:</strong> {evidence.fileName}</p>
                  {evidence.duration && (
                    <p><strong>Duration:</strong> {Math.floor(evidence.duration)}s</p>
                  )}
                  {evidence.fileSize && (
                    <p><strong>Size:</strong> {(evidence.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="no-video">Video not available</p>
            )}
          </div>
        )}

        {/* Analysis Section */}
        {license.permissions?.viewAnalysis && job && (
          <div className="content-section analysis-section">
            <h2>📊 AI Analysis</h2>
            <div className="analysis-content">
              <div className="analysis-meta">
                <div className="meta-row">
                  <strong>Model:</strong> {job.selectedModel || 'N/A'}
                </div>
                <div className="meta-row">
                  <strong>Provider:</strong> {job.provider || 'N/A'}
                </div>
                <div className="meta-row">
                  <strong>Status:</strong> 
                  <span className={`status-badge ${job.status}`}>
                    {job.status}
                  </span>
                </div>
                {job.completedAt && (
                  <div className="meta-row">
                    <strong>Completed:</strong> {job.completedAt.toDate().toLocaleString()}
                  </div>
                )}
              </div>

              {/* Display job results */}
              {job.detections && (
                <div className="detections-section">
                  <h3>Detection Results:</h3>
                  <div className="detections-display">
                    <pre>{JSON.stringify(job.detections, null, 2)}</pre>
                  </div>
                </div>
              )}

              {job.summary && (
                <div className="summary-section">
                  <h3>Summary:</h3>
                  <p>{job.summary}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Permissions Summary */}
        <div className="content-section permissions-section">
          <h2>🔑 Your Permissions</h2>
          <div className="permissions-grid">
            <div className={`permission-item ${license.permissions?.viewAnalysis ? 'enabled' : 'disabled'}`}>
              <span className="permission-icon">📊</span>
              <span className="permission-label">View Analysis</span>
              <span className="permission-status">
                {license.permissions?.viewAnalysis ? '✓' : '✗'}
              </span>
            </div>
            <div className={`permission-item ${license.permissions?.viewVideo ? 'enabled' : 'disabled'}`}>
              <span className="permission-icon">📹</span>
              <span className="permission-label">Watch Video</span>
              <span className="permission-status">
                {license.permissions?.viewVideo ? '✓' : '✗'}
              </span>
            </div>
            <div className={`permission-item ${license.permissions?.exportData ? 'enabled' : 'disabled'}`}>
              <span className="permission-icon">💾</span>
              <span className="permission-label">Export Data</span>
              <span className="permission-status">
                {license.permissions?.exportData ? '✓' : '✗'}
              </span>
            </div>
            <div className={`permission-item ${license.permissions?.screenshotFrames ? 'enabled' : 'disabled'}`}>
              <span className="permission-icon">📸</span>
              <span className="permission-label">Screenshot Frames</span>
              <span className="permission-status">
                {license.permissions?.screenshotFrames ? '✓' : '✗'}
              </span>
            </div>
            <div className={`permission-item disabled`}>
              <span className="permission-icon">⬇️</span>
              <span className="permission-label">Download Video</span>
              <span className="permission-status">✗</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseViewer;

